// src/pages/Invoices/Footer.jsx
import { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Typography, Button, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import styles from './index.module.css';
import { fromCents } from '../../../utils/totals';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getDepositInvoiceDisplayAllocation, isDepositInvoiceRole } from '../../../utils/invoiceDeposits';
import { getIssuedPaymentSummary, shouldShowIssuedPaymentSummary } from '../../../utils/invoicePaymentSummary';
import { normalizeInvoiceRoles } from '../../../utils/invoicePermissions';
import { resolveUsableInvoiceCheckoutLink } from '../../../utils/invoiceCheckoutLink';
import { resolveOtherServiceCustomerAmounts } from '../../../utils/otherServicePricing';

const TEXT_GREY = '#55656d';

const fmtMoney = (n) => '$' + (Number(n) || 0).toFixed(2);
const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const toCents = (v) => Math.round(toNumber(v) * 100);
const formatPct = (v) => {
  if (!Number.isFinite(v)) return '';
  return `${v.toFixed(2).replace(/\.00$/, '').replace(/(\.\d*[1-9])0$/, '$1')}%`;
};

const formatTaxComponentLabel = (row = {}) => {
  const label = String(row?.label || 'Tax').trim() || 'Tax';
  const rate = Number(row?.rate);
  if (!Number.isFinite(rate) || rate <= 0) return label.toUpperCase();
  const pct = (rate * 100).toFixed(3).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  return `${label} (${pct}%)`.toUpperCase();
};

const formatAddressLines = (address) => {
  if (!address) return [];
  const line1 = address.addressLine1 || '';
  const line2 = address.addressLine2 || '';
  const city = address.city || '';
  const state = address.stateProvince || '';
  const postal = address.postalCode || '';
  const country = address.country || '';
  const cityLine = [city, state].filter(Boolean).join(', ');
  const postalLine = [postal, country].filter(Boolean).join(' ');
  return [line1, line2, cityLine, postalLine].filter(Boolean);
};

const parseRel = (rel) => {
  if (!rel) return {};
  if (typeof rel === 'string') {
    try { return JSON.parse(rel); } catch { return {}; }
  }
  return rel;
};
const centsToAmount = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n / 100 : 0;
};

const firstPositiveAmount = (...values) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};


const summarizeItemTaxDetailsList = (items = []) => (Array.isArray(items) ? items : []).map((item, index) => ({
  index,
  itemId: item?.id ?? item?.ProductId ?? item?.productId ?? null,
  name: item?.Name ?? item?.name ?? null,
  taxIds: Array.isArray(item?.taxIds) ? item.taxIds : [],
  taxDetailsList: (Array.isArray(item?.taxDetailsList) ? item.taxDetailsList : [])
    .map((tax) => ({
      taxId: tax?.taxId ?? tax?.TaxId ?? tax?.id ?? null,
      type: tax?.type ?? null,
      stateCode: tax?.stateCode ?? null,
      countryCode: tax?.countryCode ?? null,
      rate: tax?.rate ?? tax?.value ?? null,
    })),
}));

const prorateTaxBreakdown = (rows = [], ratio = 1, targetAmount = null) => {
  const next = (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    taxCents: Math.round((Number(row?.taxCents) || 0) * ratio),
    taxableSubtotalCents: Math.round((Number(row?.taxableSubtotalCents) || 0) * ratio),
  })).filter((row) => row.taxCents > 0);

  const targetCents = targetAmount == null ? null : toCents(targetAmount);
  if (next.length && Number.isFinite(targetCents)) {
    const actualCents = next.reduce((sum, row) => sum + row.taxCents, 0);
    const drift = targetCents - actualCents;
    if (drift !== 0 && Math.abs(drift) <= 5) {
      const largest = next.reduce((max, row) => (row.taxCents > max.taxCents ? row : max), next[0]);
      largest.taxCents += drift;
    }
  }

  return next;
};

const buildInvoiceSubtotalBreakdown = (inv) => {
  const safeNum = (v, d = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };

  const items = Array.isArray(inv?.items) ? inv.items : [];
  const otherServices = Array.isArray(inv?.otherServices) ? inv.otherServices : [];
  const parts = [];

  items.forEach((product, pIdx) => {
    const pricings = Array.isArray(product?.pricings) ? product.pricings : [];
    const artworks = Array.isArray(product?.artworks) ? product.artworks : [];
    const decorations = Array.isArray(product?.decorations) ? product.decorations : [];

    pricings.forEach((pr, idx) => {
      const qty = safeNum(pr?.Quantity, 0);
      const retail = safeNum(pr?.Retail, 0);
      const rowTotal = pr?.Total != null ? safeNum(pr.Total, retail * qty) : retail * qty;
      parts.push({ source: 'item.pricing', productIndex: pIdx, rowIndex: idx, qty, retail, total: rowTotal });
    });

    decorations.forEach((deco, dIdx) => {
      const charges = Array.isArray(deco?.Charge) ? deco.Charge : [];
      charges.forEach((ch, cIdx) => {
        const chQty = safeNum(ch?.Quantity, 0);
        const chRetail = safeNum(ch?.Retail, 0);
        const fallbackTotal = ch?.Total != null ? safeNum(ch.Total, chRetail * chQty) : chRetail * chQty;
        const chTotal = chRetail > 0 ? chRetail * chQty : fallbackTotal;
        parts.push({ source: 'item.decoration.charge', productIndex: pIdx, decorationIndex: dIdx, chargeIndex: cIdx, qty: chQty, retail: chRetail, total: chTotal });
      });
    });

    artworks.forEach((art, aIdx) => {
      if (art?.Total != null) {
        parts.push({ source: 'item.artwork.total', productIndex: pIdx, artworkIndex: aIdx, total: safeNum(art.Total, 0) });
        return;
      }
      const charge = safeNum(art?.Charge, 0);
      const matchedQty = safeNum(pricings?.[aIdx]?.Quantity, 0);
      parts.push({ source: 'item.artwork.chargeXqty', productIndex: pIdx, artworkIndex: aIdx, charge, qty: matchedQty, total: charge * matchedQty });
    });
  });

  otherServices.forEach((svc, sIdx) => {
    const subtotalNoTax =
      svc?.Subtotal != null ? safeNum(svc.Subtotal, 0) :
      svc?.subTotal != null ? safeNum(svc.subTotal, 0) :
      svc?.subtotal != null ? safeNum(svc.subtotal, 0) :
      null;
    // Diagnostic subtotal must use the same customer-price precedence as the
    // invoice tax breakdown and displayed service line. Preserve the raw
    // subtotalNoTax above so a stale zero remains visible for troubleshooting.
    const total = resolveOtherServiceCustomerAmounts(svc).subtotal;
    parts.push({
      source: 'otherService',
      serviceIndex: sIdx,
      quantity: safeNum(svc?.Quantity, 0),
      unitCost: safeNum(svc?.UnitCost, 0),
      subtotalNoTax,
      rawTotal: safeNum(svc?.Total, 0),
      total,
    });
  });

  return {
    parts,
    // Debug presentation uses cents, just like the authoritative invoice totals.
    recomputedSubtotal: Math.round(parts.reduce((sum, p) => sum + safeNum(p.total, 0), 0) * 100) / 100,
  };
};

const normalizePaymentStatusLabel = (status) => {
  const s = String(status || '').trim().toLowerCase();
  if (!s) return '';
  if (['paid', 'completed', 'succeeded', 'success'].includes(s)) return 'Paid';
  if (['open', 'pending', 'required', 'requires_payment', 'payment_required', 'unpaid'].includes(s)) return 'Open';
  if (['not_required', 'n/a', 'na'].includes(s)) return 'Not Required';
  if (['failed', 'canceled', 'cancelled', 'void'].includes(s)) return 'Failed';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const isPaidStatus = (status) => {
  const s = String(status || '').trim().toLowerCase();
  return ['paid', 'completed', 'succeeded', 'success'].includes(s);
};


const decodeBase64Url = (str) => {
  try {
    const pad = '='.repeat((4 - (str.length % 4)) % 4);
    const base64 = (str + pad).replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    const text = decodeURIComponent(
      json
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const getRoleFromAccessToken = () => {
  const token = localStorage.getItem('accessToken');
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payload = decodeBase64Url(parts[1]);
  return payload?.role ?? payload?.user?.role ?? null;
};

const PAYMENT_OPTION = Object.freeze({
  STRIPE: 'stripe',
  MANUAL: 'manual',
  CUSTOMER_SELECTION: 'customer_selection',
});

const normalizeInvoicePaymentOption = (invoice = {}) => {
  const paymentMethod = String(invoice?.paymentMethod || '').trim().toLowerCase();
  if (paymentMethod === PAYMENT_OPTION.CUSTOMER_SELECTION) return PAYMENT_OPTION.CUSTOMER_SELECTION;
  if (paymentMethod === PAYMENT_OPTION.MANUAL || paymentMethod === 'others' || invoice?.paymentType) return PAYMENT_OPTION.MANUAL;
  if (paymentMethod === PAYMENT_OPTION.STRIPE || invoice?.checkoutLink || invoice?.checkoutSessionId) return PAYMENT_OPTION.STRIPE;
  return PAYMENT_OPTION.CUSTOMER_SELECTION;
};

const Footer = ({
  invoiceDetails,
  authState = {},
  totalsCents, // { subtotalCents, taxCents, totalCents, units, effectiveTaxRate, taxCountryCode, taxStateCode }
  taxBreakdown = [],
  orderContextTotals,
  footerAddress,
  termsHtml,
  onRefreshInvoice,
  paymentReadiness = { status: 'unsigned' },
}) => {
  const currency = String(invoiceDetails?.currency || 'CAD').toUpperCase();

  const subtotal = totalsCents ? fromCents(totalsCents.subtotalCents) : 0;
  const taxAmount = totalsCents ? fromCents(totalsCents.taxCents) : 0;
  const totalOrderValue = totalsCents ? fromCents(totalsCents.totalCents) : 0;
  const adjustmentTotal = (Array.isArray(invoiceDetails?.adjustments) ? invoiceDetails.adjustments : [])
    .reduce((sum, adjustment) => {
      const resolved = Number(adjustment?.resolvedAmount);
      return sum + (Number.isFinite(resolved) ? resolved : 0);
    }, 0);
  const hasAdjustments = Math.abs(adjustmentTotal) >= 0.005;
  const preAdjustmentSubtotal = (value) => Math.max(0, Number(value || 0) - adjustmentTotal);
  const taxAdjustmentSuffix = hasAdjustments ? ' (AFTER ADJUSTMENTS)' : '';
  const rel = parseRel(invoiceDetails?.invoiceRelationships);
  const accountingRef = parseRel(invoiceDetails?.accountingRef);
  const invoiceHistory = accountingRef?.thirdParty?.invoiceHistory || {};
  const invoiceRole = String(rel?.role || rel?.type || '').trim().toLowerCase();
  const isDepositInvoice = isDepositInvoiceRole(invoiceDetails);
  const isPartialInvoice = ['partial', 'partial-invoice'].includes(invoiceRole);
  const isProgressInvoice = ['progress', 'progress-invoice'].includes(invoiceRole);
  const isPartialLikeInvoice = isPartialInvoice || isProgressInvoice;
  const isBalanceInvoice = ['balance', 'balance-invoice'].includes(invoiceRole);
  const isAmountBasedInvoice = isDepositInvoice || isPartialLikeInvoice || isBalanceInvoice;
  const isInvoicingCartInvoice = Boolean(rel?.invoicingCartKey || rel?.renderSnapshot?.source === 'invoicing-cart' || rel?.renderSnapshot?.cartKey);
  const invoiceAmount = Number(invoiceDetails?.amount);
  const issuedPaymentSummary = getIssuedPaymentSummary(invoiceDetails);
  const showIssuedPaymentSummary = shouldShowIssuedPaymentSummary(invoiceDetails);
  const isCurrentlyPaid = isPaidStatus(invoiceDetails?.paymentStatus) || isPaidStatus(invoiceDetails?.invoiceStatus) || paymentReadiness?.status === 'paid';
  const resolvedAmountBasedInvoiceAmount = isBalanceInvoice
    ? firstPositiveAmount(
      rel?.currentInvoiceAmount,
      rel?.remainingBalance,
      rel?.activeInvoiceTotal,
      centsToAmount(rel?.balanceClassification?.balanceCents),
      centsToAmount(rel?.expectedTotalCents),
      centsToAmount(invoiceHistory?.currentInvoiceAmountCents),
      centsToAmount(invoiceHistory?.expectedTotalCents),
      invoiceAmount
    )
    : firstPositiveAmount(
      invoiceAmount,
      rel?.currentInvoiceAmount,
      centsToAmount(invoiceHistory?.currentInvoiceAmountCents)
    );
  const manualPartialAmount = isPartialLikeInvoice ? resolvedAmountBasedInvoiceAmount : 0;
  const displayTotal = isPartialLikeInvoice && manualPartialAmount > 0
    ? manualPartialAmount
    : isAmountBasedInvoice && resolvedAmountBasedInvoiceAmount > 0
      ? resolvedAmountBasedInvoiceAmount
      : totalOrderValue;

  // Derive deposit % from displayed deposit amount vs the invoice order total.
  // Use cents to avoid floating-point drift (e.g., 49.91% vs 50% artifacts).
  const relRemainingBalance = toNumber(rel?.remainingBalance);
  const relDepositApplied = toNumber(rel?.depositAppliedAmount);
  const relOrderTotalCents = toCents(relRemainingBalance + relDepositApplied);
  const relTotals = rel?.thirdParty?.totals || rel?.totals || {};
  const canonicalOrderSubtotal = (() => {
    const cands = isInvoicingCartInvoice
      ? [
        subtotal,
        toNumber(invoiceDetails?.subtotal),
        toNumber(rel?.orderSubtotal),
        toNumber(rel?.subtotal),
        centsToAmount(relTotals?.subtotalCents),
        centsToAmount(relTotals?.itemCents),
        toNumber(orderContextTotals?.subtotal),
        0,
      ]
      : [
        toNumber(orderContextTotals?.subtotal),
        toNumber(rel?.orderSubtotal),
        toNumber(rel?.subtotal),
        centsToAmount(relTotals?.subtotalCents),
        centsToAmount(relTotals?.itemCents),
        0,
      ];
    return cands.find((v) => Number.isFinite(v) && v > 0) || 0;
  })();
  const canonicalOrderTotalInclTax = (() => {
    const cands = isInvoicingCartInvoice
      ? [
        totalOrderValue,
        toNumber(invoiceDetails?.total),
        resolvedAmountBasedInvoiceAmount > 0 ? resolvedAmountBasedInvoiceAmount : 0,
        toNumber(rel?.orderTotal),
        toNumber(rel?.total),
        centsToAmount(relTotals?.totalCents),
        toNumber(orderContextTotals?.totalInclTax),
        toNumber(relRemainingBalance + relDepositApplied),
        0,
      ]
      : [
        toNumber(orderContextTotals?.totalInclTax),
        toNumber(rel?.orderTotal),
        toNumber(rel?.total),
        centsToAmount(relTotals?.totalCents),
        toNumber(relRemainingBalance + relDepositApplied),
        0,
      ];
    return cands.find((v) => Number.isFinite(v) && v > 0) || 0;
  })();
  const invoiceOrderTotalCents = totalsCents?.totalCents || 0;
  const displayTotalCents = toCents(displayTotal);

  const effectiveOrderTotalCents = isAmountBasedInvoice
    ? (
      canonicalOrderTotalInclTax > 0
        ? toCents(canonicalOrderTotalInclTax)
        : (invoiceOrderTotalCents > 0 ? invoiceOrderTotalCents : relOrderTotalCents)
    )
    : toCents(totalOrderValue);
  const effectiveOrderTotal = fromCents(effectiveOrderTotalCents);

  const amountRatioFromOrderTotal = isAmountBasedInvoice && effectiveOrderTotalCents > 0
    ? Math.min(1, Math.max(0, displayTotalCents / effectiveOrderTotalCents))
    : 1;
  const displaySubtotal = isAmountBasedInvoice ? subtotal * amountRatioFromOrderTotal : subtotal;
  const displayTaxAmount = isAmountBasedInvoice ? taxAmount * amountRatioFromOrderTotal : taxAmount;
  const remainingBalance = isAmountBasedInvoice
    ? Math.max(0, fromCents(effectiveOrderTotalCents - displayTotalCents))
    : Math.max(0, effectiveOrderTotal - displayTotal);
  const orderTotalContext = effectiveOrderTotal;
  const amountPercent = isAmountBasedInvoice && effectiveOrderTotalCents > 0
    ? (displayTotalCents / effectiveOrderTotalCents) * 100
    : null;
  const isFullAmountPartialLike = isPartialLikeInvoice &&
    effectiveOrderTotalCents > 0 &&
    displayTotalCents === effectiveOrderTotalCents;
  const orderSubtotalContext = (() => {
    const candidates = [
      canonicalOrderSubtotal,
      Math.max(0, effectiveOrderTotal - taxAmount),
      effectiveOrderTotal,
    ];
    return candidates.find((v) => Number.isFinite(v) && v > 0) || 0;
  })();
  const amountDueLabel = (() => {
    const relPct = toNumber(rel?.depositPercent || rel?.percent || rel?.depositPercentage);
    if (isDepositInvoice) {
      if (isCurrentlyPaid) return 'Deposit Paid';
      if (relPct > 0) return `Deposit Due (${formatPct(relPct)})`;
      if (amountPercent != null) return `Deposit Due (${formatPct(amountPercent)})`;
      return 'Deposit Due';
    }
    const percentLabel = amountPercent != null ? ` (${formatPct(amountPercent)})` : '';
    if (isFullAmountPartialLike) return 'Amount Due';
    if (isPartialInvoice) return `Partial Invoice Due${percentLabel}`;
    if (isProgressInvoice) return `Progress Invoice Due${percentLabel}`;
    if (isBalanceInvoice) return `Balance Due${percentLabel}`;
    return `Amount Due${percentLabel}`;
  })();
  const taxDueLabel = (() => {
    if (isDepositInvoice) return 'Tax On Deposit';
    if (isFullAmountPartialLike) return 'Tax';
    if (isPartialInvoice) return 'Tax On Partial Invoice';
    if (isProgressInvoice) return 'Tax On Progress Invoice';
    if (isBalanceInvoice) return 'Tax On Balance';
    return 'Tax';
  })();
  const amountRatio = isAmountBasedInvoice && effectiveOrderTotalCents > 0
    ? Math.min(1, Math.max(0, displayTotalCents / effectiveOrderTotalCents))
    : 1;
  const amountDuePreTaxEstimate = isPartialLikeInvoice ? displayTotal : subtotal * amountRatio;
  const amountTaxDue = isPartialLikeInvoice ? 0 : taxAmount * amountRatio;
  const checkoutAmountDue = isCurrentlyPaid
    ? 0
    : showIssuedPaymentSummary
      ? Math.max(0, Number(issuedPaymentSummary?.balanceDue || 0))
      : Math.max(0, Number(displayTotal || 0));
  const displayTaxBreakdown = isPartialLikeInvoice
    ? []
    : prorateTaxBreakdown(
      taxBreakdown,
      isAmountBasedInvoice ? amountRatio : 1,
      isAmountBasedInvoice ? amountTaxDue : displayTaxAmount
    );
  // Issued/full balance summaries display the frozen gross tax. Show its
  // individual tax types only when they reconcile exactly to that same amount.
  const issuedSummaryTaxCents = toCents(Math.max(0,
    issuedPaymentSummary.grossInvoiceTotal - orderSubtotalContext
  ));
  const issuedTaxGroups = Array.isArray(taxBreakdown) &&
    taxBreakdown.length > 0 &&
    taxBreakdown.reduce((sum, row) => sum + (Number(row?.taxCents) || 0), 0) === issuedSummaryTaxCents
      ? taxBreakdown : [];
  // Standard invoices also fall back to a neutral TAX row if legacy line
  // metadata cannot explain the canonical saved tax amount.
  const standardTaxGroups = displayTaxBreakdown.length &&
    displayTaxBreakdown.reduce((sum, row) => sum + (Number(row?.taxCents) || 0), 0) === toCents(displayTaxAmount)
      ? displayTaxBreakdown : [];
  // The immutable paid/deposit amount is the display authority. Allocate GST
  // first, then let the pre-tax deposit line absorb the rounding cent. This
  // keeps pre-tax + tax == the historical paid amount without regeneration.
  const depositDisplayAllocation = isDepositInvoice
    ? getDepositInvoiceDisplayAllocation({
      amountCents: displayTotalCents,
      proratedTaxRows: displayTaxBreakdown,
      fallbackTaxCents: toCents(amountTaxDue),
    })
    : null;
  const amountDuePreTax = depositDisplayAllocation
    ? fromCents(depositDisplayAllocation.subtotalCents)
    : amountDuePreTaxEstimate;
  const summedTaxBreakdownCents = useMemo(
    () => (Array.isArray(taxBreakdown) ? taxBreakdown : [])
      .reduce((sum, row) => sum + (Number(row?.taxCents) || 0), 0),
    [taxBreakdown]
  );
  const targetTaxCents = Number(totalsCents?.taxCents ?? 0) || 0;
  const differenceCents = summedTaxBreakdownCents - targetTaxCents;
  const itemTaxDetailsListSummary = useMemo(
    () => summarizeItemTaxDetailsList(invoiceDetails?.items),
    [invoiceDetails?.items]
  );
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (!Array.isArray(taxBreakdown) || !taxBreakdown.length) return;
    if (differenceCents === 0) return;
    // eslint-disable-next-line no-console
    console.warn('[GeneratedInvoice/Footer taxBreakdown] Tax breakdown cents do not match totalsCents.taxCents.', {
      invoiceId: invoiceDetails?.id || null,
      taxBreakdown,
      totalsTaxCents: targetTaxCents,
      summedTaxBreakdownCents,
      differenceCents,
    });
  }, [differenceCents, invoiceDetails?.id, summedTaxBreakdownCents, targetTaxCents, taxBreakdown]);

  const invoiceSubtotalBreakdown = buildInvoiceSubtotalBreakdown(invoiceDetails);
  const taxBreakdownDebugPayload = {
    invoiceId: invoiceDetails?.id || null,
    taxBreakdown,
    totalsTaxCents: targetTaxCents,
    'totalsCents.taxCents': targetTaxCents,
    summedTaxBreakdownCents,
    differenceCents,
    itemTaxDetailsListSummary,
  };
  const contextDebugPayload = {
    invoiceId: invoiceDetails?.id || null,
    orderId: invoiceDetails?.orderId || invoiceDetails?.OrderId || null,
    relationshipOrderId: rel?.orderId || null,
    relationshipSalesOrderId: rel?.salesOrderId || null,
    sourceOrderContextTotals: orderContextTotals || null,
    canonicalOrderSubtotal,
    canonicalOrderTotalInclTax,
    orderSubtotalContext,
    orderTotalContext,
    displayTotal,
    invoiceRole,
    isAmountBasedInvoice,
    amountRatioFromOrderTotal: amountRatio,
    subtotalFromInvoiceRows: subtotal,
    subtotalBreakdown: invoiceSubtotalBreakdown.parts,
    subtotalBreakdownRecomputed: invoiceSubtotalBreakdown.recomputedSubtotal,
    taxFromInvoiceRows: taxAmount,
    totalFromInvoiceRows: totalOrderValue,
    taxBreakdownDiagnostics: taxBreakdownDebugPayload,
  };


  const companyName =
    footerAddress?.companyName || 'Elite Promotional Marketing (Elite Sportswear & Awards Ltd)';
  const addressLines = formatAddressLines(footerAddress);
  const paymentOption = normalizeInvoicePaymentOption(invoiceDetails);
  const [generatedCheckoutLink, setGeneratedCheckoutLink] = useState('');
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);
  const [paymentLinkError, setPaymentLinkError] = useState('');
  const [paymentLinkCopied, setPaymentLinkCopied] = useState(false);
  const [copyPaymentLinkError, setCopyPaymentLinkError] = useState('');

  const storedCheckoutLink = resolveUsableInvoiceCheckoutLink(invoiceDetails);
  const checkoutLink = generatedCheckoutLink || storedCheckoutLink || null;
  const isVoidedInvoice = ['void', 'voided', 'cancelled', 'canceled'].includes(
    String(invoiceDetails?.invoiceStatus || '').trim().toLowerCase()
  );
  const paymentStatusRaw = String(invoiceDetails?.paymentStatus || invoiceDetails?.invoiceStatus || '').trim();
  const paymentStatus = isVoidedInvoice ? 'Voided' : normalizePaymentStatusLabel(paymentStatusRaw);
  const hasManualPaymentDetails =
    Number(invoiceDetails?.paymentReceivedAmount || 0) > 0 ||
    Boolean(invoiceDetails?.paymentReceivedDate) ||
    Boolean(invoiceDetails?.paymentNotes);
  const isPaid = isCurrentlyPaid;
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('[GeneratedInvoice/Footer paid state]', {
      invoiceId: invoiceDetails?.id,
      invoiceStatus: invoiceDetails?.invoiceStatus,
      paymentStatus: invoiceDetails?.paymentStatus,
      paymentReceivedAmount: invoiceDetails?.paymentReceivedAmount,
      paymentReceivedDate: invoiceDetails?.paymentReceivedDate,
      paymentNotes: invoiceDetails?.paymentNotes,
      hasManualPaymentDetails,
      isPaid,
    });
  }
  useEffect(() => {
    setGeneratedCheckoutLink(resolveUsableInvoiceCheckoutLink(invoiceDetails) || '');
  }, [
    invoiceDetails?.checkoutLink, invoiceDetails?.checkoutUrl,
    invoiceDetails?.paymentLink, invoiceDetails?.stripeCheckoutUrl,
    invoiceDetails?.linkExpiry,
  ]);

  useEffect(() => {
    setPaymentLinkCopied(false);
    setCopyPaymentLinkError('');
  }, [checkoutLink]);

  const hasManualOnlyPaymentAction = paymentOption === PAYMENT_OPTION.MANUAL;
  const hasCustomerSelectionPaymentAction = paymentOption === PAYMENT_OPTION.CUSTOMER_SELECTION;
  const hasAppliedDeposit = Number(issuedPaymentSummary?.priorDepositPaid || 0) > 0;
  const canPayOutstandingInvoice = !isPaid && !isVoidedInvoice && !hasManualOnlyPaymentAction && checkoutAmountDue > 0;
  const verifiedCheckoutAmount = paymentReadiness?.status === 'ready' &&
    Number.isFinite(Number(paymentReadiness.amountDue)) &&
    Math.abs(Number(paymentReadiness.amountDue) - checkoutAmountDue) < 0.005;
  const signedLinkUnverified = paymentReadiness?.status === 'error';
  const canOpenExistingLink = canPayOutstandingInvoice && !signedLinkUnverified && Boolean(checkoutLink);
  const canGenerateNewLink = canPayOutstandingInvoice && (
    verifiedCheckoutAmount ||
    // Preserve the authenticated finance workflow on direct invoice URLs.
    // The protected internal endpoint still validates amount and permissions.
    (paymentReadiness?.status === 'unsigned' && authState?.status === true)
  );
  const paymentCardAmount = isPaid
    ? firstPositiveAmount(invoiceDetails?.paymentReceivedAmount, invoiceDetails?.amount, checkoutAmountDue)
    : checkoutAmountDue;
  const hasCheckoutGate = Boolean(
    checkoutLink ||
    paymentStatus ||
    hasManualOnlyPaymentAction ||
    hasCustomerSelectionPaymentAction
  );
  // The same instance role inheritance as the Invoice tab. The token handles
  // direct generated-invoice URLs when the React auth context is not hydrated.
  // Do not use a stale userRole string on unauthenticated/public invoice links.
  const contextRoles = normalizeInvoiceRoles(authState);
  // An explicit non-admin instance session takes priority over stale token
  // claims. Token-only fallback is for direct generated-invoice links before
  // the instance auth context has loaded.
  const canViewDebugContext = contextRoles.size > 0
    ? contextRoles.has('administrator')
    : normalizeInvoiceRoles({ role: getRoleFromAccessToken() }).has('administrator');

  const handleGeneratePaymentLink = async () => {
    if (!invoiceDetails?.id || generatingPaymentLink || !canGenerateNewLink) return;
    setGeneratingPaymentLink(true);
    setPaymentLinkError('');
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams(window.location.search || '');
      const reconcileToken = String(params.get('prt') || '').trim();
      const usePublicEndpoint = Boolean(reconcileToken);
      const endpoint = usePublicEndpoint
        ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/invoices/public/${invoiceDetails.id}/payment-link?prt=${encodeURIComponent(reconcileToken)}`
        : `${process.env.REACT_APP_NODEJS_SERVER_URL}/invoices/${invoiceDetails.id}/renew-checkout-link`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { accessToken: token } : {}),
        },
        body: usePublicEndpoint ? JSON.stringify({ prt: reconcileToken }) : JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || 'Unable to generate a credit card payment link right now.');
      }

      const nextLink = data?.invoice?.checkoutLink || '';
      if (!nextLink) throw new Error('The payment provider did not return a payment link. Please try again.');
      setGeneratedCheckoutLink(nextLink);
      if (typeof onRefreshInvoice === 'function') {
        await onRefreshInvoice();
      }
    } catch (error) {
      setPaymentLinkError(error?.message || 'Unable to generate a credit card payment link right now.');
    } finally {
      setGeneratingPaymentLink(false);
    }
  };

  const copyPaymentLink = async () => {
    if (!checkoutLink) return;
    setCopyPaymentLinkError('');
    try {
      await navigator.clipboard.writeText(checkoutLink);
      setPaymentLinkCopied(true);
    } catch {
      setPaymentLinkCopied(false);
      setCopyPaymentLinkError('Unable to copy this link automatically. Please use Pay by credit card to open it.');
    }
  };

  return (
    <>
      <Grid container className={styles.footerContainer}>
        <Grid md={6} xs={12}>
          <Typography variant="h6">Questions about this invoice?</Typography>
          <Box>
            <Typography color={TEXT_GREY} variant="body2">Accounting</Typography>
            <Typography color={TEXT_GREY} variant="body2">accounting@elitepromomarketing.com</Typography>
            <Typography color={TEXT_GREY} variant="body2">(780) 454-9775</Typography>
          </Box>
          <Box>
            <Typography color={TEXT_GREY} variant="body2" sx={{ fontWeight: 600 }}>
              {companyName}
            </Typography>
            {addressLines.length ? (
              addressLines.map((line) => (
                <Typography key={line} color={TEXT_GREY} variant="body2">
                  {line}
                </Typography>
              ))
            ) : (
              <>
                <Typography color={TEXT_GREY} variant="body2">14703-118 Ave NW</Typography>
                <Typography color={TEXT_GREY} variant="body2">Edmonton, Alberta T5L 2M7</Typography>
                <Typography color={TEXT_GREY} variant="body2">Canada</Typography>
              </>
            )}
          </Box>
        </Grid>

        <Grid item md={6} xs={12} className={styles.totalPriceContainer}>
          {showIssuedPaymentSummary ? (
            <>
              <Box className={styles.titleValue}>
                <Typography variant="body1" className={styles.title} gutterBottom>SUBTOTAL</Typography>
                <Typography variant="body1" className={styles.value}>{fmtMoney(hasAdjustments ? preAdjustmentSubtotal(orderSubtotalContext) : orderSubtotalContext)}</Typography>
              </Box>
              {hasAdjustments && (
                <Box className={styles.titleValue} data-testid="invoice-adjustments-row">
                  <Typography variant="body1" className={styles.title} gutterBottom>ADJUSTMENTS (BEFORE TAX)</Typography>
                  <Typography variant="body1" className={styles.value} sx={{ color: '#b9ffb7' }}>
                    {`${currency} ${adjustmentTotal.toFixed(2)}`}
                  </Typography>
                </Box>
              )}
              {issuedTaxGroups.length ? issuedTaxGroups.map((row) => (
                <Box className={styles.titleValue} key={row.key}>
                  <Typography variant="body1" className={styles.title} gutterBottom>
                    {`TAX - ${formatTaxComponentLabel(row)}${taxAdjustmentSuffix}`}
                  </Typography>
                  <Typography variant="body1" className={`${styles.value} ${styles.GSTContainer}`}>
                    {fmtMoney(fromCents(row.taxCents))}
                  </Typography>
                </Box>
              )) : (
                <Box className={styles.titleValue}>
                  <Typography variant="body1" className={styles.title} gutterBottom>TAX</Typography>
                  <Typography variant="body1" className={`${styles.value} ${styles.GSTContainer}`}>
                    {fmtMoney(fromCents(issuedSummaryTaxCents))}
                  </Typography>
                </Box>
              )}
              <Box className={styles.titleValue}>
                <Typography variant="body1" className={styles.title} gutterBottom>TOTAL</Typography>
                <Typography variant="body1" className={styles.value}>{`${currency} ${issuedPaymentSummary.grossInvoiceTotal.toFixed(2)}`}</Typography>
              </Box>
              {issuedPaymentSummary.priorDepositPaid > 0 && (
                <Box className={styles.titleValue} data-testid="invoice-deposit-paid-row">
                  <Typography variant="body1" className={styles.title} gutterBottom>DEPOSIT PAID</Typography>
                  <Typography variant="body1" className={styles.value} sx={{ color: '#b9ffb7' }}>{`${currency} -${issuedPaymentSummary.priorDepositPaid.toFixed(2)}`}</Typography>
                </Box>
              )}
              {issuedPaymentSummary.priorOtherPayments > 0 && (
                <Box className={styles.titleValue} data-testid="invoice-other-payments-row">
                  <Typography variant="body1" className={styles.title} gutterBottom>OTHER PAYMENTS</Typography>
                  <Typography variant="body1" className={styles.value} sx={{ color: 'success.main' }}  >{`${currency} -${issuedPaymentSummary.priorOtherPayments.toFixed(2)}`}</Typography>
                </Box>
              )}
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1, pt: 1 }}>
                <Box className={styles.titleValue} data-testid="invoice-balance-due-row">
                  <Typography variant="body1" className={styles.title} gutterBottom>{isVoidedInvoice ? 'ORIGINAL BALANCE (VOIDED)' : 'BALANCE DUE'}</Typography>
                  <Typography variant="body1" className={styles.value}>{`${currency} ${checkoutAmountDue.toFixed(2)}`}</Typography>
                </Box>
              </Box>
            </>
          ) : isAmountBasedInvoice ? (
            <>
              <Box className={styles.titleValue}>
                <Typography variant="body1" className={styles.title} gutterBottom>ORDER SUBTOTAL</Typography>
                <Typography variant="body1" className={styles.value}>
                  {`${currency} ${(hasAdjustments ? preAdjustmentSubtotal(orderSubtotalContext) : orderSubtotalContext).toFixed(2)}`}
                </Typography>
              </Box>
              {hasAdjustments && (
                <Box className={styles.titleValue} data-testid="invoice-adjustments-row">
                  <Typography variant="body1" className={styles.title} gutterBottom>ADJUSTMENTS (BEFORE TAX)</Typography>
                  <Typography variant="body1" className={styles.value} sx={{ color: '#b9ffb7' }}>
                    {`${currency} ${adjustmentTotal.toFixed(2)}`}
                  </Typography>
                </Box>
              )}
              <Box className={styles.titleValue}>
                <Typography variant="body1" className={styles.title} gutterBottom>ORDER TOTAL (INCL TAX)</Typography>
                <Typography variant="body1" className={styles.value}>{`${currency} ${orderTotalContext.toFixed(2)}`}</Typography>
              </Box>
              <Box className={styles.titleValue}>
                <Typography variant="body1" className={styles.title} gutterBottom>{amountDueLabel.toUpperCase()}</Typography>
                <Typography variant="body1" className={styles.value}>{`${currency} ${amountDuePreTax.toFixed(2)}`}</Typography>
              </Box>
              {!isPartialLikeInvoice && (displayTaxBreakdown.length ? (
                displayTaxBreakdown.map((row) => (
                  <Box className={styles.titleValue} key={row.key}>
                    <Typography variant="body1" className={styles.title} gutterBottom>{`${taxDueLabel} - ${formatTaxComponentLabel(row)}${taxAdjustmentSuffix}`.toUpperCase()}</Typography>
                    <Typography variant="body1" className={`${styles.value} ${styles.GSTContainer}`}>
                      {fmtMoney(fromCents(row.taxCents))}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box className={styles.titleValue}>
                  <Typography variant="body1" className={styles.title} gutterBottom>{taxDueLabel.toUpperCase()}</Typography>
                  <Typography variant="body1" className={`${styles.value} ${styles.GSTContainer}`}>
                    {fmtMoney(amountTaxDue)}
                  </Typography>
                </Box>
              ))}
              <Box className={styles.titleValue}>
                <Typography variant="body1" className={styles.title} gutterBottom>{isVoidedInvoice ? 'ORIGINAL INVOICE TOTAL' : 'TOTAL DUE NOW'}</Typography>
                <Typography variant="body1" className={styles.value}>
                  {`${currency} ${displayTotal.toFixed(2)}`}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box className={styles.titleValue}>
                <Typography variant="body1" className={styles.title} gutterBottom>SUBTOTAL</Typography>
                <Typography variant="body1" className={styles.value}>{fmtMoney(hasAdjustments ? preAdjustmentSubtotal(displaySubtotal) : displaySubtotal)}</Typography>
              </Box>

              {hasAdjustments && (
                <Box className={styles.titleValue} data-testid="invoice-adjustments-row">
                  <Typography variant="body1" className={styles.title} gutterBottom>ADJUSTMENTS (BEFORE TAX)</Typography>
                  <Typography variant="body1" className={styles.value} sx={{ color: '#b9ffb7' }}>
                    {`${currency} ${adjustmentTotal.toFixed(2)}`}
                  </Typography>
                </Box>
              )}

              {standardTaxGroups.length ? (
                standardTaxGroups.map((row) => (
                  <Box className={styles.titleValue} key={row.key}>
                    <Typography variant="body1" className={styles.title} gutterBottom>{`TAX - ${formatTaxComponentLabel(row)}${taxAdjustmentSuffix}`}</Typography>
                    <Typography variant="body1" className={`${styles.value} ${styles.GSTContainer}`}>
                      {fmtMoney(fromCents(row.taxCents))}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box className={styles.titleValue}>
                  <Typography variant="body1" className={styles.title} gutterBottom>{`TAX${taxAdjustmentSuffix}`}</Typography>
                  <Typography variant="body1" className={`${styles.value} ${styles.GSTContainer}`}>
                    {fmtMoney(displayTaxAmount)}
                  </Typography>
                </Box>
              )}

              <Box className={styles.titleValue}>
                <Typography variant="body1" className={styles.title} gutterBottom>TOTAL</Typography>
                <Typography variant="body1" className={styles.value}>
                  {`${currency} ${displayTotal.toFixed(2)}`}
                </Typography>
              </Box>
            </>
          )}

          {hasCheckoutGate && (
            <Box className={styles.paymentCard} data-testid="invoice-payment-card">
              <Box className={styles.paymentCardHeading}>
                <Box className={styles.paymentCardIcon} aria-hidden="true">
                  {isPaid ? <CheckCircleOutlineIcon fontSize="small" /> : <CreditCardOutlinedIcon fontSize="small" />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h3" className={styles.paymentCardTitle}>
                    {isVoidedInvoice ? 'Invoice voided' : isPaid ? 'Payment received' : 'Pay this invoice'}
                  </Typography>
                  <Typography className={styles.paymentCardDescription}>
                    {isVoidedInvoice
                      ? 'This invoice has been voided and is no longer payable.'
                      : isPaid
                      ? 'Thank you. Your payment has been recorded.'
                      : isDepositInvoice
                        ? 'Pay your deposit securely using the available payment options.'
                        : hasAppliedDeposit
                          ? 'Your deposit has been applied. The remaining balance is ready for payment.'
                          : 'The outstanding balance is ready for payment.'}
                  </Typography>
                </Box>
              </Box>

              <Box className={styles.paymentCardMeta}>
                <Typography component="p" className={styles.paymentCardAmount}>
                  {isVoidedInvoice ? 'Original invoice amount:' : isPaid ? 'Amount paid:' : 'Amount due:'}{' '}
                  <Box component="strong">{currency} {paymentCardAmount.toFixed(2)}</Box>
                </Typography>
                <Box className={styles.paymentCardStatus}>
                  <Typography component="span" className={styles.paymentCardStatusLabel}>Status:</Typography>
                  <Box
                    component="span"
                    className={`${styles.paymentCardStatusBadge} ${isPaid ? styles.paymentCardStatusPaid : isVoidedInvoice || paymentStatus === 'Failed' ? styles.paymentCardStatusFailed : styles.paymentCardStatusOpen}`}
                  >
                    {isVoidedInvoice ? 'Voided' : isPaid ? 'Paid' : paymentStatus || 'Open'}
                  </Box>
                </Box>
              </Box>

              {canOpenExistingLink && (
                <Box className={styles.paymentCardActions}>
                  <Button
                    component="a"
                    href={checkoutLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    disableElevation
                    className={styles.paymentCardPayButton}
                    startIcon={<CreditCardOutlinedIcon fontSize="small" />}
                    endIcon={<ArrowForwardIosIcon sx={{ fontSize: 13 }} />}
                  >
                    Pay by credit card
                  </Button>
                  <Button
                    variant="outlined"
                    className={styles.paymentCardCopyButton}
                    startIcon={<LinkOutlinedIcon fontSize="small" />}
                    onClick={copyPaymentLink}
                    aria-label="Copy payment link"
                  >
                    {paymentLinkCopied ? 'Link copied' : 'Copy payment link'}
                  </Button>
                </Box>
              )}

              {canPayOutstandingInvoice && hasCustomerSelectionPaymentAction && !checkoutLink && (
                <Box className={styles.paymentCardActions}>
                  <Button
                    variant="contained"
                    disableElevation
                    className={styles.paymentCardPayButton}
                    startIcon={<CreditCardOutlinedIcon fontSize="small" />}
                    onClick={handleGeneratePaymentLink}
                    disabled={generatingPaymentLink || !canGenerateNewLink}
                  >
                    {generatingPaymentLink ? 'Generating link...' : 'Generate payment link'}
                  </Button>
                </Box>
              )}

              {canPayOutstandingInvoice && !checkoutLink &&
                paymentReadiness?.status !== 'ready' && (
                <Typography role="alert" className={styles.paymentCardError}>
                  {paymentReadiness?.status === 'error'
                    ? paymentReadiness.message || 'Unable to verify your payment amount. Please retry or contact Accounting.'
                    : 'To generate a secure payment link, please open the latest invoice email or contact Accounting.'}
                </Typography>
              )}
              {copyPaymentLinkError && (
                <Typography role="alert" className={styles.paymentCardError}>
                  {copyPaymentLinkError}
                </Typography>
              )}
              {paymentLinkError && !isPaid && (
                <Typography role="alert" className={styles.paymentCardError}>
                  {paymentLinkError}
                </Typography>
              )}
              {!isPaid && hasManualPaymentDetails && (
                <Typography className={styles.paymentCardNote}>
                  Manual payment details have been entered, but this invoice has not been marked paid yet.
                </Typography>
              )}

              {!isPaid && (
                <Box className={styles.paymentCardFooter}>
                  {checkoutAmountDue > 0 ? (
                    <Typography className={styles.paymentCardHelper}>
                      For EFT, wire transfer, cheque, or other payment methods, please contact our team.
                    </Typography>
                  ) : (
                    <Typography className={styles.paymentCardHelper}>No payment is currently due.</Typography>
                  )}
                  {isDepositInvoice && checkoutAmountDue > 0 && remainingBalance > 0 && (
                    <Typography className={styles.paymentCardHelper}>
                      The remaining order balance of {currency} {remainingBalance.toFixed(2)} will be requested when it is due.
                    </Typography>
                  )}
                  {checkoutLink && checkoutAmountDue > 0 && (
                    <Typography className={styles.paymentCardHelper}>
                      You can copy the payment link to share it if needed.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
      {/* ───────────────────────── New Terms & Conditions ───────────────────────── */}

      {termsHtml?.trim() ? (
        <Grid className={styles.termsContainer}>
          <Box dangerouslySetInnerHTML={{ __html: termsHtml }} />
        </Grid>
      ) : (
        <Grid className={styles.termsContainer}>
          <Box>
            <Typography className={styles.Titleheading}>Terms and Conditions</Typography>
          </Box>

          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              Thank you very much for your order. We appreciate your business!
            </Typography>
          </Box>

          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              Please remit payment to us as soon as possible based on the payment terms noted.
              We accept all major credit cards (Visa, M/C, American Express), cheques, EFT, and cash.
            </Typography>
          </Box>

          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              We now also accept E-transfer to{' '}
              <a href="mailto:Payment@elitepromomarketing.com">Payment@elitepromomarketing.com</a>.
            </Typography>
          </Box>

          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              If you have any questions regarding this invoice, please contact your Elite Sales Representative
              or the Elite head office by calling <a href="tel:+17804549775">1-780-454-9775</a>.
            </Typography>
          </Box>

          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              Invoiced in CANADIAN DOLLARS (CDN) unless otherwise specified.
            </Typography>
          </Box>

          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              GST # 101610855RT0001
            </Typography>
          </Box>
        </Grid>
      )}


      {canViewDebugContext && (
        <Grid className={`${styles.termsContainer} ${styles.screenOnly}`}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Debug: Order Context & Tax Breakdown</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
                {JSON.stringify({ key: 'taxBreakdown', payload: taxBreakdownDebugPayload }, null, 2)}
              </Typography>
              <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', mt: 2 }}>
                {JSON.stringify({ key: 'orderSubtotalContext', value: orderSubtotalContext, payload: contextDebugPayload }, null, 2)}
              </Typography>
              <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', mt: 2 }}>
                {JSON.stringify({ key: 'orderTotalContext', value: orderTotalContext, payload: contextDebugPayload }, null, 2)}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
      {/* ───────────────────────────────────────────────────────────────────────── */}
    </>
  );
};

export default Footer;
